import { useState, useEffect } from 'react';
import type { GameType } from '@big2/shared';
import { Icon } from '../Icon.js';
import { CardUI } from '../CardUI.js';
import { DaaluuColorPicker } from '../game/daaluu/DaaluuColorPicker.js';
import { useI18n } from '../../i18n.js';

interface LobbyActionsFooterProps {
  isHost: boolean;
  isFinished: boolean;
  canStart: boolean;
  gameType: GameType;
  hasHistory: boolean;
  hasExistingScores?: boolean;
  matchOver?: boolean;
  onStartGame: (options?: { restartMatch?: boolean }) => void;
  onRestartMatch?: () => void;
  onLeaveRoom: () => void;
  onOpenHistory?: () => void;
  onOpenCardBacks?: () => void;
}

export function LobbyActionsFooter({
  isHost,
  isFinished,
  canStart,
  gameType,
  hasHistory,
  hasExistingScores,
  matchOver,
  onStartGame,
  onRestartMatch,
  onLeaveRoom,
  onOpenHistory,
  onOpenCardBacks,
}: LobbyActionsFooterProps) {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const [isPendingStart, setIsPendingStart] = useState(false);

  useEffect(() => {
    if (!isPendingStart) return;
    const timer = setTimeout(() => setIsPendingStart(false), 3000);
    return () => clearTimeout(timer);
  }, [isPendingStart]);

  return (
    <footer className="lobby-launchpad">
      <div className="lobby-tools-group">
        {/* Leave Room Button */}
        <button
          type="button"
          className="icon-button lobby-leave-btn"
          onClick={onLeaveRoom}
          aria-label={t.leave}
          title={t.leave}
          data-tooltip={t.leave}
          data-testid="lobby-leave-btn"
        >
          <Icon name="log-out" />
        </button>

        {/* Change Card Back Button */}
        {gameType === 'daaluu' ? (
          <DaaluuColorPicker />
        ) : (
          onOpenCardBacks && (
            <button
              type="button"
              className="icon-button card-back-tool"
              onClick={onOpenCardBacks}
              aria-label={t.changeCardBack}
              title={t.changeCardBack}
              data-tooltip={t.changeCardBack}
            >
              <span>
                <CardUI faceDown style={{ transform: 'scale(0.28)', transformOrigin: 'top left' }} />
              </span>
            </button>
          )
        )}

        {/* Match History Button */}
        {hasHistory && onOpenHistory && (
          <button
            type="button"
            className="room-secondary lobby-history-btn"
            onClick={onOpenHistory}
          >
            <Icon name="list-ordered" size={15} />
            <span>{isMn ? 'Үр дүн' : 'Results'}</span>
          </button>
        )}
      </div>

      <div className="lobby-primary-action-wrap">
        {isHost ? (
          <>
            {hasExistingScores && onRestartMatch && !matchOver && (
              <button
                type="button"
                className="room-secondary lobby-restart-match-btn"
                onClick={onRestartMatch}
                disabled={!canStart}
                title={isMn ? 'Оноог тэглэж шинэ тэмцээн эхлүүлэх' : 'Reset points and start new match'}
              >
                <Icon name="rotate-ccw" size={16} />
                <span>{t.startNewMatch}</span>
              </button>
            )}

            <button
              type="button"
              className="lobby-btn-start"
              onClick={() => {
                setIsPendingStart(true);
                if (matchOver && onRestartMatch) {
                  onRestartMatch();
                } else {
                  onStartGame();
                }
              }}
              disabled={!canStart || isPendingStart}
            >
              <Icon name={matchOver ? 'rotate-ccw' : 'play'} size={18} />
              <span>
                {isPendingStart
                  ? isMn ? 'Эхлүүлж байна...' : 'Starting...'
                  : matchOver
                  ? t.startNewMatch
                  : isFinished
                  ? t.playAgain
                  : t.startGame}
              </span>
            </button>
          </>
        ) : (
          <div className="lobby-waiting-status">
            <span className="lobby-waiting-spinner" />
            <span>{isMn ? 'Эзэн тоглоомыг эхлүүлэхийг хүлээж байна...' : 'Waiting for host to start...'}</span>
          </div>
        )}
      </div>
    </footer>
  );
}
